/* ============================================================
   DRIP theme · global.js
   Cart drawer, variant/bundle selection, sticky ATC, accordions,
   mobile nav, quantity steppers, gallery, marquee clone.
   Vanilla JS, no dependencies.
   ============================================================ */
(function () {
  "use strict";

  const money = (cents) => {
    const fmt = window.Drip && window.Drip.moneyFormat ? window.Drip.moneyFormat : "${{amount}}";
    const amount = (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
  };

  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------------- Toast ---------------- */
  let toastEl;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "toast";
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add("is-visible");
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => toastEl.classList.remove("is-visible"), 2200);
  }

  /* ---------------- Cart API ---------------- */
  const Cart = {
    async get() {
      const r = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
      return r.json();
    },
    async add(items) {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.description || "Could not add to cart"); }
      return r.json();
    },
    async change(line, quantity) {
      const r = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ line, quantity }),
      });
      return r.json();
    },
  };

  function updateCartCount(count) {
    qsa("[data-cart-count]").forEach((el) => {
      el.textContent = count;
      el.classList.toggle("hidden", count === 0);
    });
  }

  /* ---------------- Cart drawer ---------------- */
  const drawer = qs("[data-cart-drawer]");
  function openDrawer() { if (drawer) { drawer.setAttribute("aria-hidden", "false"); document.body.style.overflow = "hidden"; renderDrawer(); } }
  function closeDrawer() { if (drawer) { drawer.setAttribute("aria-hidden", "true"); document.body.style.overflow = ""; } }

  async function renderDrawer() {
    if (!drawer) return;
    const body = qs("[data-drawer-body]", drawer);
    const foot = qs("[data-drawer-subtotal]", drawer);
    const cart = await Cart.get();
    updateCartCount(cart.item_count);
    if (cart.item_count === 0) {
      body.innerHTML = '<p class="text-dim" style="padding:2rem 0;text-align:center">Your cart is empty.</p>';
      if (foot) foot.textContent = money(0);
      return;
    }
    body.innerHTML = cart.items.map((it) => `
      <div class="cart-line" data-line-key="${it.key}">
        <div class="cart-line__media">${it.image ? `<img src="${it.image}" alt="${it.product_title}" loading="lazy">` : ""}</div>
        <div>
          <div class="cart-line__title">${it.product_title}</div>
          ${it.variant_title && it.variant_title !== "Default Title" ? `<div class="cart-line__variant">${it.variant_title}</div>` : ""}
          <div class="qty" data-qty style="margin-top:.5rem">
            <button type="button" data-qty-minus aria-label="Decrease">−</button>
            <input type="text" value="${it.quantity}" data-qty-input readonly>
            <button type="button" data-qty-plus aria-label="Increase">+</button>
          </div>
        </div>
        <div style="text-align:right">
          <div style="font-weight:700">${money(it.final_line_price)}</div>
          <button class="cart-line__remove" data-remove>Remove</button>
        </div>
      </div>`).join("");
    if (foot) foot.textContent = money(cart.total_price);
  }

  document.addEventListener("click", async (e) => {
    if (e.target.closest("[data-cart-toggle]")) { e.preventDefault(); openDrawer(); }
    if (e.target.closest("[data-drawer-close]") || e.target.closest("[data-drawer-overlay]")) closeDrawer();

    const line = e.target.closest("[data-line-key]");
    if (line) {
      const key = line.getAttribute("data-line-key");
      if (e.target.closest("[data-remove]")) { await Cart.change(key, 0); renderDrawer(); toast("Removed from cart"); }
      if (e.target.closest("[data-qty-plus]") || e.target.closest("[data-qty-minus]")) {
        const input = qs("[data-qty-input]", line);
        let q = parseInt(input.value, 10) || 1;
        q += e.target.closest("[data-qty-plus]") ? 1 : -1;
        q = Math.max(0, q);
        await Cart.change(key, q); renderDrawer();
      }
    }
  });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") { closeDrawer(); closeMobileNav(); } });

  /* ---------------- Add to cart forms ---------------- */
  qsa("[data-product-form]").forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const btn = qs('[type="submit"]', form);
      const id = qs('[name="id"]', form).value;
      const qtyInput = qs('[name="quantity"]', form);
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (!id) { toast("Please select an option"); return; }
      btn.classList.add("is-loading");
      try {
        await Cart.add([{ id: Number(id), quantity }]);
        toast("Added to cart");
        openDrawer();
      } catch (err) { toast(err.message); }
      finally { btn.classList.remove("is-loading"); }
    });
  });

  /* ---------------- Variant selection (PDP) ---------------- */
  qsa("[data-variant-selector]").forEach((root) => {
    let variants = [];
    try { variants = JSON.parse(qs("[data-variant-json]", root).textContent); } catch (e) {}
    const optionInputs = qsa("[data-option-index]", root);

    function selectedOptions() {
      const opts = [];
      qsa("[data-option-group]", root).forEach((group) => {
        const checked = qs("input:checked", group) || qs("select", group);
        if (checked) opts[Number(group.getAttribute("data-option-group"))] = checked.value;
      });
      return opts;
    }

    function findVariant(opts) {
      return variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }

    function update() {
      const opts = selectedOptions();
      const variant = findVariant(opts) || variants.find((v) => v.available) || variants[0];
      if (!variant) return;
      const idField = qs('[name="id"]', root.closest("[data-product-form]") || document);
      if (idField) idField.value = variant.id;

      // price
      const priceEl = qs("[data-price-current]", root);
      const compareEl = qs("[data-price-compare]", root);
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price); compareEl.classList.remove("hidden");
        } else { compareEl.classList.add("hidden"); }
      }
      // labels
      qsa("[data-selected-value]", root).forEach((el) => {
        const idx = Number(el.getAttribute("data-selected-value"));
        el.textContent = opts[idx] || "";
      });
      // availability
      const btn = qs('[data-atc-text]', document);
      const submit = qs('[type="submit"]', root.closest("[data-product-form]") || document);
      if (submit) {
        if (variant.available) { submit.disabled = false; if (btn) btn.textContent = submit.getAttribute("data-add-label") || "Add to cart"; }
        else { submit.disabled = true; if (btn) btn.textContent = "Sold out"; }
      }
      // sticky bar mirror
      const stickyPrice = qs("[data-sticky-price]");
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      // swap gallery to variant image
      if (variant.featured_media_id) {
        const thumb = qs(`[data-media-id="${variant.featured_media_id}"]`);
        if (thumb) thumb.click();
      }
    }

    root.addEventListener("change", update);
    update();
  });

  /* ---------------- Quantity steppers (PDP standalone) ---------------- */
  qsa("[data-qty]:not([data-line-key] [data-qty])").forEach((wrap) => {
    const input = qs("[data-qty-input]", wrap);
    if (!input || input.hasAttribute("readonly")) return;
    qs("[data-qty-minus]", wrap)?.addEventListener("click", () => { input.value = Math.max(1, (parseInt(input.value,10)||1) - 1); });
    qs("[data-qty-plus]", wrap)?.addEventListener("click", () => { input.value = (parseInt(input.value,10)||1) + 1; });
  });

  /* ---------------- Product media gallery ---------------- */
  qsa("[data-gallery]").forEach((gallery) => {
    const mainImg = qs("[data-gallery-main] img", gallery);
    qsa("[data-media-id]", gallery).forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const src = thumb.getAttribute("data-full") || qs("img", thumb)?.src;
        if (src && mainImg) mainImg.src = src;
        qsa("[data-media-id]", gallery).forEach((t) => t.classList.remove("is-active"));
        thumb.classList.add("is-active");
      });
    });
  });

  /* ---------------- Accordions ---------------- */
  qsa(".accordion__trigger").forEach((trigger) => {
    trigger.addEventListener("click", () => {
      const expanded = trigger.getAttribute("aria-expanded") === "true";
      const panel = trigger.nextElementSibling;
      trigger.setAttribute("aria-expanded", String(!expanded));
      panel.style.maxHeight = expanded ? null : panel.scrollHeight + "px";
    });
  });

  /* ---------------- Sticky add-to-cart bar ---------------- */
  const sticky = qs("[data-sticky-atc]");
  const anchor = qs("[data-atc-anchor]");
  if (sticky && anchor && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle("is-visible", !entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    io.observe(anchor);
    qs("[data-sticky-add]", sticky)?.addEventListener("click", () => {
      qs('[data-product-form] [type="submit"]')?.click();
    });
  }

  /* ---------------- Mobile nav ---------------- */
  const mnav = qs("[data-mobile-nav]");
  function closeMobileNav() { if (mnav) { mnav.setAttribute("aria-hidden", "true"); document.body.style.overflow = ""; } }
  qs("[data-nav-toggle]")?.addEventListener("click", () => { mnav.setAttribute("aria-hidden", "false"); document.body.style.overflow = "hidden"; });
  qsa("[data-mnav-close]", mnav || document).forEach((el) => el.addEventListener("click", closeMobileNav));
  qs("[data-mnav-overlay]", mnav || document)?.addEventListener("click", closeMobileNav);

  /* ---------------- Marquee: duplicate track for seamless loop ---------------- */
  qsa(".marquee").forEach((m) => {
    const track = qs(".marquee__track", m);
    if (track) { const clone = track.cloneNode(true); clone.setAttribute("aria-hidden", "true"); m.appendChild(clone); }
  });

  /* ---------------- init ---------------- */
  Cart.get().then((c) => updateCartCount(c.item_count)).catch(() => {});
})();
